import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageSquare, Mail, FileText, Calendar, Play, Loader2, Star, UserPlus } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { openRecording } from '@/utils/openRecording';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { useCustomerIdsByPhone } from '@/hooks/useCustomers';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Communication } from '@/types/communications';

interface LogsTableProps {
  communications: Communication[];
}

function RecordingButton({ callId, recordingUrl }: { callId: string; recordingUrl?: string }) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleClick = async () => {
    if (recordingUrl) {
      window.open(recordingUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setLoading(true);
    const err = await openRecording(callId);
    if (err) toast({ title: 'Recording unavailable', description: err, variant: 'destructive' });
    setLoading(false);
  };

  return (
    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleClick} disabled={loading}>
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 text-primary" />}
    </Button>
  );
}

const typeIcons = {
  call: Phone,
  sms: MessageSquare,
  email: Mail,
  note: FileText,
  meeting: Calendar,
};

const typeColors = {
  call: 'bg-info/10 text-info',
  sms: 'bg-success/10 text-success',
  email: 'bg-primary/10 text-primary',
  note: 'bg-warning/10 text-warning',
  meeting: 'bg-destructive/10 text-destructive',
};

const formatDuration = (seconds?: number) => {
  if (!seconds) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

function StarRating({ score }: { score?: number }) {
  if (!score) return <span className="text-muted-foreground text-xs">-</span>;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-3 w-3 ${star <= score
            ? 'fill-warning text-warning'
            : 'text-muted-foreground/30'
            }`}
        />
      ))}
    </div>
  );
}

export function LogsTable({ communications }: LogsTableProps) {
  // Phone fallback for log rows that carry no customer_id.
  const resolveCustomerId = useCustomerIdsByPhone(communications);

  return (
    <TooltipProvider>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[130px]">Date/Time</TableHead>
              <TableHead className="w-[80px]">Type</TableHead>
              <TableHead className="w-[60px]">Dir</TableHead>
              <TableHead className="w-[60px]">New?</TableHead>
              <TableHead className="w-[80px]">Score</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="w-[120px]">Phone</TableHead>
              <TableHead className="w-[80px]">Talk Time</TableHead>
              <TableHead className="w-[60px]">Play</TableHead>
              <TableHead className="w-[120px]">SID</TableHead>
              <TableHead className="w-[120px]">Source</TableHead>
              <TableHead className="w-[100px]">Agent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {communications.map((communication) => {
              const Icon = typeIcons[communication.type];
              const colorClass = typeColors[communication.type];

              return (
                <TableRow key={`${communication.source}-${communication.id}`}>
                  {/* Date/Time */}
                  <TableCell className="text-sm whitespace-nowrap">
                    {format(new Date(communication.timestamp), 'd MMM yyyy')}
                    <br />
                    <span className="text-muted-foreground text-xs">
                      {format(new Date(communication.timestamp), 'h:mm a')}
                    </span>
                  </TableCell>

                  {/* Type */}
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <div className={`p-1 rounded-full ${colorClass}`}>
                        <Icon className="h-3 w-3" />
                      </div>
                      <span className="text-xs capitalize">{communication.type}</span>
                    </div>
                  </TableCell>

                  {/* Direction */}
                  <TableCell>
                    {communication.direction ? (
                      <Badge
                        variant={communication.direction === 'inbound' ? 'success' : 'info'}
                        className="whitespace-nowrap px-2 py-0"
                      >
                        {communication.direction === 'inbound' ? 'Incoming' : 'Outgoing'}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>

                  {/* Is New Caller */}
                  <TableCell>
                    {communication.type === 'call' && communication.is_new_caller !== undefined ? (
                      communication.is_new_caller ? (
                        <Badge variant="success" className="cursor-default px-1.5 py-0">
                          New
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">Existing</span>
                      )
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>

                  {/* Call Score */}
                  <TableCell>
                    {communication.type === 'call' ? (
                      <StarRating score={communication.call_score} />
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>

                  {/* Customer */}
                  <TableCell>
                    <div className="font-medium text-sm truncate max-w-[150px]">
                      {/* Customer card first; fall back to the lead when this
                          caller has no customer record yet. Router links, so
                          neither one reloads the whole app. */}
                      {resolveCustomerId(communication) ? (
                        <CustomerLink
                          customerId={resolveCustomerId(communication)}
                          name={communication.customer_name}
                          className="text-primary"
                        />
                      ) : communication.lead_id ? (
                        <Link to={`/leads/${communication.lead_id}`} className="hover:underline text-primary">
                          {communication.customer_name}
                        </Link>
                      ) : (
                        communication.customer_name
                      )}
                    </div>
                    {communication.lead_number && (
                      <span className="text-xs text-muted-foreground">{communication.lead_number}</span>
                    )}
                  </TableCell>

                  {/* Phone */}
                  <TableCell className="text-sm text-muted-foreground font-mono text-xs">
                    {communication.customer_phone || '-'}
                  </TableCell>

                  {/* Talk Time */}
                  <TableCell className="text-sm text-muted-foreground">
                    {communication.type === 'call'
                      ? formatDuration(communication.talk_time_seconds)
                      : '-'}
                  </TableCell>

                  {/* Recording */}
                  <TableCell>
                    {communication.type === 'call' && (communication.talk_time_seconds || 0) > 0
                      ? <RecordingButton callId={communication.id} recordingUrl={communication.recording_url} />
                      : <span className="text-muted-foreground text-xs">-</span>
                    }
                  </TableCell>

                  {/* SID (external_call_id) */}
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {communication.call_id ? (
                      <Tooltip>
                        <TooltipTrigger className="truncate max-w-[100px] block">
                          {communication.call_id.slice(-8)}
                        </TooltipTrigger>
                        <TooltipContent>{communication.call_id}</TooltipContent>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>

                  {/* Source (lead source name) */}
                  <TableCell className="text-sm text-muted-foreground">
                    <span className="truncate max-w-[100px] block">
                      {communication.source_name || communication.campaign_name || '-'}
                    </span>
                  </TableCell>

                  {/* Agent */}
                  <TableCell className="text-sm text-muted-foreground">
                    {communication.agent_name || '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}
